import { useKpisPerimeter } from '#composables/kpisPerimeter';
import { useFiltersForQueryKey } from '#composables/useFiltersForQueryKey';
import { useLikeForLikePerimeter } from '#domain/performance/likeForLike/useLikeForLikePerimeter';
import { ApiErrorCode, type GetIndicatorResult, type IndicatorName } from '@carrefour-gcs/shared';
import { useQuery } from '@tanstack/vue-query';
import { toReactive } from '@vueuse/core';
import { type MaybeRefOrGetter } from 'vue';
import { useTrpc } from '../useTrpc';
import { prepareInput } from './indicator.utils';

/**
 * A generic data-driven interface for a indicator panel. This object is used to provide
 * enough information to the front-end to render a indicator panel. It should NOT include
 * UI-related configuration (such as the panel size, the panel color, etc.) as this is
 * and SHOULD be handled by the front-end.
 *
 * @template T The queried indicator name.
 * @example type IndicatorSalesAmount = Indicators<'salesAmount'> // => { salesAmount: Indicator }
 */
type GetIndicatorsResult<T extends IndicatorName> = Record<
  T,
  {
    error?: Error | null;
    data?: GetIndicatorResult;
    isError?: boolean;
    isFetching?: boolean;
    isInitialLoading?: boolean;
  }
>;

/**
 * Sparingly fetch the given `indicators` data based on the given `period` and `filters`. The
 * data is fetched through tRPC and appended to the returned object as soon as it is
 * available.
 *
 * By default, the `period` and `filters` are fetched from the `FiltersStore`. If you want
 * to use a different period or filters, you can pass them as arguments.
 *
 * @param indicators The indicators to fetch the data for.
 * @param options Additional options for the query.
 * @returns A reactive object containing the data, the error and the loading state.
 * @example useIndicators(['salesAmount']) // => { salesAmount: { isLoading: true } }
 */
export const useIndicators = <T extends IndicatorName>(
  indicators: T | T[],
  options?: { enabled?: MaybeRefOrGetter<boolean>; excludeFiltersFromQueryKey?: string[] }
): GetIndicatorsResult<T> => {
  // --- Prepare variables.
  const indicatorsArray = Array.isArray(indicators) ? indicators : [indicators];
  const isEnabled = options?.enabled ?? true;

  const {
    withTax: { isWithTax },
    taxonomy: { taxonomy },
    filters: { selectedFiltersKpiQuery },
    period: { selectedPeriod, comparisonPeriod },
    storePerimeter: { storePerimeter },
    myDataIncluded: { isMyDataIncluded },
    detentionPeriod: { detentionPeriod }
  } = useKpisPerimeter();

  const excludedFilterKeys = options?.excludeFiltersFromQueryKey ?? [];
  const filtersForQueryKey = useFiltersForQueryKey(selectedFiltersKpiQuery, excludedFilterKeys);
  const { resolveLikeForLikeStoreKeys } = useLikeForLikePerimeter();

  // --- Instantiate a query for each indicator.
  const results = {} as GetIndicatorsResult<T>;
  for (const indicator of indicatorsArray) {
    const result = useQuery<GetIndicatorResult, Error>({
      enabled: isEnabled,
      queryKey: [
        indicator,
        selectedPeriod,
        comparisonPeriod,
        filtersForQueryKey,
        isWithTax,
        taxonomy,
        storePerimeter,
        isMyDataIncluded,
        detentionPeriod
      ],
      queryKeyHashFn: JSON.stringify,
      queryFn: async ({ signal }) => {
        // L4L: the front-resolved perimeter is a data dependency of the request.
        const likeForLikeStoreKeys = await resolveLikeForLikeStoreKeys();

        const detentionWindowEligibleIndicators = [
          'salesPossessionRate',
          'salesPossessionRateWithChainTypes',
          'nonPossessed',
          'numberOfReferencesHeld',
          'numberOfHolderStores',
          'numberOfReferencesHeldWithTheoretical',
          'numberOfHolderStoresWithTheoretical',
          'salesAmount',
          'salesQuantity'
        ];
        const isDetentionPeriodEligible = detentionWindowEligibleIndicators.includes(indicator);

        const input = prepareInput({
          indicator,
          period: selectedPeriod,
          comparisonPeriod,
          ...(isDetentionPeriodEligible && {
            detentionPeriod: detentionPeriod.value
          }),
          filters: selectedFiltersKpiQuery,
          isWithTax,
          taxonomy,
          storePerimeter,
          isMyDataIncluded,
          likeForLikeStoreKeys
        });

        const result = await useTrpc().performance.kpi.getIndicator.mutate(input, { signal });

        if (result.status === 'success') {
          return result.data ?? {};
        }
        if (result.error === ApiErrorCode.UNAUTHORIZED_DATA_ACCESS_PERMISSIONS) {
          return {};
        }

        throw new Error(result.error);
      }
    });

    // --- Unwrap the reactive object and assign it to the results object.
    results[indicator] = toReactive(result);
  }

  // --- Return the result object.
  return results;
};
